// ============================================================================
// Payment metrics — pure functions over PaymentRecord rows.
// ============================================================================

import { PaymentRecord } from '../../services/paymentService';
import { getLocalDateString } from '../dateUtils';
import { bucketKey, bucketLabel, enumerateBuckets, groupBy, median, sumBy } from './aggregations';
import { AnalysisStay, Granularity, SeriesPoint, isRevenueStay } from './types';

const SUCCESS_STATUSES = new Set(['completed', 'succeeded', 'success', 'captured']);
const FAILURE_STATUSES = new Set(['failed', 'declined', 'error']);

export function isSuccessfulPayment(payment: PaymentRecord): boolean {
  return SUCCESS_STATUSES.has(payment.status);
}

export function isFailedPayment(payment: PaymentRecord): boolean {
  return FAILURE_STATUSES.has(payment.status);
}

export function isRefundPayment(payment: PaymentRecord): boolean {
  return payment.payment_type === 'refund' || payment.flow_type === 'refund';
}

/** Local YYYY-MM-DD for a payment's creation timestamp. */
export function paymentDate(payment: PaymentRecord): string | null {
  const iso = payment.created_at || payment.initiated_at;
  if (!iso) return null;
  return getLocalDateString(new Date(iso));
}

export function paymentsInWindow(payments: PaymentRecord[], from: string, to: string): PaymentRecord[] {
  return payments.filter(p => {
    const date = paymentDate(p);
    return date !== null && date >= from && date <= to;
  });
}

// ============================================================================
// Failures
// ============================================================================

export interface CountRow {
  label: string;
  count: number;
  amount: number;
}

export function failuresByErrorCode(payments: PaymentRecord[]): CountRow[] {
  const failed = payments.filter(isFailedPayment);
  const grouped = groupBy(failed, p => p.last_error_code || 'unknown');
  return Array.from(grouped.entries())
    .map(([label, items]) => ({ label, count: items.length, amount: sumBy(items, i => i.amount) }))
    .sort((a, b) => b.count - a.count);
}

export function failuresByGateway(payments: PaymentRecord[]): CountRow[] {
  const failed = payments.filter(isFailedPayment);
  const grouped = groupBy(failed, p => p.gateway_provider || 'unknown');
  return Array.from(grouped.entries())
    .map(([label, items]) => ({ label, count: items.length, amount: sumBy(items, i => i.amount) }))
    .sort((a, b) => b.count - a.count);
}

/** Failure share of attempted (success + failed) payments, as a percent. */
export function failureRate(payments: PaymentRecord[]): number | null {
  const attempted = payments.filter(p => isSuccessfulPayment(p) || isFailedPayment(p));
  if (attempted.length === 0) return null;
  const failed = attempted.filter(isFailedPayment).length;
  return Math.round((failed / attempted.length) * 1000) / 10;
}

// ============================================================================
// Refunds & volume
// ============================================================================

export function refundSeries(
  payments: PaymentRecord[],
  from: string,
  to: string,
  granularity: Granularity
): SeriesPoint[] {
  return amountSeries(payments.filter(p => isRefundPayment(p) && isSuccessfulPayment(p)), from, to, granularity);
}

export function collectedSeries(
  payments: PaymentRecord[],
  from: string,
  to: string,
  granularity: Granularity
): SeriesPoint[] {
  return amountSeries(payments.filter(p => isSuccessfulPayment(p) && !isRefundPayment(p)), from, to, granularity);
}

function amountSeries(
  payments: PaymentRecord[],
  from: string,
  to: string,
  granularity: Granularity
): SeriesPoint[] {
  const buckets = enumerateBuckets(from, to, granularity);
  const totals = new Map<string, number>(buckets.map(b => [b, 0]));
  for (const payment of payments) {
    const date = paymentDate(payment);
    if (!date || date < from || date > to) continue;
    const key = bucketKey(date, granularity);
    if (totals.has(key)) totals.set(key, totals.get(key)! + payment.amount);
  }
  return buckets.map(key => ({
    bucket: key,
    label: bucketLabel(key, granularity),
    value: Math.round(totals.get(key)! * 100) / 100,
  }));
}

// ============================================================================
// Payment method mix
// ============================================================================

export function methodMix(payments: PaymentRecord[]): CountRow[] {
  const successful = payments.filter(p => isSuccessfulPayment(p) && !isRefundPayment(p));
  const grouped = groupBy(successful, p => {
    if (p.card_brand) return p.card_brand;
    return p.payment_method || p.gateway_provider || 'other';
  });
  return Array.from(grouped.entries())
    .map(([label, items]) => ({ label, count: items.length, amount: sumBy(items, i => i.amount) }))
    .sort((a, b) => b.amount - a.amount);
}

// ============================================================================
// Time to payment (booking created → payment processed)
// ============================================================================

export function medianHoursToPayment(
  stays: AnalysisStay[],
  payments: PaymentRecord[]
): number | null {
  const firstPaymentByReservation = new Map<string, PaymentRecord>();
  for (const payment of payments) {
    if (!payment.reservation_id || !isSuccessfulPayment(payment) || isRefundPayment(payment)) continue;
    const processed = payment.processed_at || payment.confirmed_at || payment.created_at;
    if (!processed) continue;
    const existing = firstPaymentByReservation.get(payment.reservation_id);
    const existingTime = existing && (existing.processed_at || existing.confirmed_at || existing.created_at);
    if (!existing || (existingTime && processed < existingTime)) {
      firstPaymentByReservation.set(payment.reservation_id, payment);
    }
  }

  const hours: number[] = [];
  for (const stay of stays) {
    if (!isRevenueStay(stay) || !stay.createdAt) continue;
    const payment = firstPaymentByReservation.get(String(stay.id));
    if (!payment) continue;
    const processed = payment.processed_at || payment.confirmed_at || payment.created_at!;
    const diffMs = new Date(processed).getTime() - new Date(stay.createdAt).getTime();
    if (diffMs >= 0) hours.push(diffMs / 36e5);
  }
  if (hours.length === 0) return null;
  return Math.round(median(hours) * 10) / 10;
}

// ============================================================================
// Outstanding balances (reservation cost vs successful non-refund payments)
// ============================================================================

export interface OutstandingBalance {
  reservationId: number;
  confirmationCode: string;
  checkIn: string;
  cost: number;
  paid: number;
  balance: number;
}

export function outstandingBalances(
  stays: AnalysisStay[],
  payments: PaymentRecord[]
): OutstandingBalance[] {
  const paidByReservation = new Map<string, number>();
  for (const payment of payments) {
    if (!payment.reservation_id) continue;
    if (isSuccessfulPayment(payment)) {
      const sign = isRefundPayment(payment) ? -1 : 1;
      paidByReservation.set(
        payment.reservation_id,
        (paidByReservation.get(payment.reservation_id) ?? 0) + sign * payment.amount
      );
    }
  }
  const rows: OutstandingBalance[] = [];
  for (const stay of stays) {
    if (!isRevenueStay(stay)) continue;
    const paid = paidByReservation.get(String(stay.id)) ?? 0;
    const balance = Math.round((stay.cost - paid) * 100) / 100;
    if (balance > 0.5) {
      rows.push({
        reservationId: stay.id,
        confirmationCode: stay.confirmationCode,
        checkIn: stay.checkIn,
        cost: stay.cost,
        paid,
        balance,
      });
    }
  }
  return rows.sort((a, b) => b.balance - a.balance);
}
